import { useEffect, useRef } from 'react';
import type { AppEventDetailMap } from '../events/appEvents';

/**
 * Subscribe to a global app event (CustomEvent on window) with a stable
 * listener. The latest handler is kept in a ref to avoid stale closures
 * and re-subscribing on every render.
 */
export function useAppEvent<K extends keyof AppEventDetailMap>(
  name: K,
  handler: (detail: AppEventDetailMap[K], event: CustomEvent<AppEventDetailMap[K]>) => void,
): void {
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    const listener = (event: Event) => {
      const customEvent = event as CustomEvent<AppEventDetailMap[K]>;
      handlerRef.current(customEvent.detail, customEvent);
    };

    window.addEventListener(name, listener as unknown as EventListener);
    return () => {
      window.removeEventListener(name, listener as unknown as EventListener);
    };
  }, [name]);
}

